/**
 * CollapsibleCard — iter82.4.6 → iter84.56.3
 *
 * Generic expand/collapse card used across the Release Ops tab to reduce
 * visual noise. Click the header to toggle the body open/closed.
 *
 * iter84.56.3 — Unified Release Ops card shell. Three additions:
 *   1. `verdict` prop drives the outline color (green / amber / red /
 *      info). Replaces the per-card ad-hoc border styling that drifted
 *      across ValuesEngineCard, UATRunbookCard, ReleaseOpsAuditCards,
 *      and DeploymentReadinessBanner over many iters.
 *   2. `autoOpenOnFail` — when verdict='fail' the card forces open on
 *      mount so red signals are never hidden behind a collapsed shell.
 *      Honors user-clicked collapse afterwards (one-shot auto-open).
 *   3. `headerExtra` already existed — used for refresh buttons etc.
 *
 * Used by:
 *   - Runbook Gate (RunbookGatePanel wrap, iter84.56.3)
 *   - Deployment Readiness (DeploymentReadinessBanner wrap, iter84.56.3)
 *   - Values Engine + UAT Runbook (ValuesAndUATCards, iter84.56.3)
 *   - Recent Deploys / Open PRs / Drift Watch (existing)
 *   - Test Pollution Scan + Pre-flight Ledger (ReleaseOpsAuditCards, iter84.56.3)
 *   - Recent Promotions / Promotion Archive (in RunbookGatePanel.jsx)
 *
 * Lives in its own file to avoid a circular import between ReleaseOpsTab
 * and RunbookGatePanel (which both consume it).
 */
import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

// iter84.56.3 — Verdict → outline + bg color map. The keys match the
// existing release-ops API verdict vocabulary so callers can pass the
// API field directly (`overall_status`, `verdict`, etc.). The card's
// internal pill colors are still controlled by `badgeClass` so each
// card keeps its own semantics for the in-pill status text.
const VERDICT_CLASS = {
    clear:   "border-emerald-500/40 bg-emerald-500/[0.03]",
    ok:      "border-emerald-500/40 bg-emerald-500/[0.03]",
    pass:    "border-emerald-500/40 bg-emerald-500/[0.03]",
    warn:    "border-amber-500/40 bg-amber-500/[0.03]",
    ack_required: "border-amber-500/40 bg-amber-500/[0.03]",
    fail:    "border-rose-500/50 bg-rose-500/[0.04]",
    block_arm: "border-rose-500/50 bg-rose-500/[0.04]",
    error:   "border-rose-500/50 bg-rose-500/[0.04]",
    // info / unknown / null → default neutral outline.
    info:    "border-zinc-800",
};

function verdictToClass(verdict) {
    if (!verdict) return VERDICT_CLASS.info;
    return VERDICT_CLASS[verdict] || VERDICT_CLASS.info;
}

function isFailVerdict(verdict) {
    return verdict === "fail" || verdict === "block_arm" || verdict === "error";
}

export function CollapsibleCard({
    icon: Icon,
    title,
    subtitle,
    badge,
    badgeClass,
    children,
    defaultOpen = false,
    testId,
    headerExtra,
    // iter84.56.3 — Verdict drives the card outline color. Pass the API
    // verdict string directly: "clear" / "ok" / "warn" / "ack_required"
    // / "fail" / "block_arm" / "error" / "info" / null.
    verdict = null,
    // iter84.56.3 — When verdict='fail' (or block_arm/error) auto-open
    // the card once on mount/verdict-change so red signals are never
    // hidden behind a collapsed shell. After the user manually
    // collapses, we don't re-pop it open from the same fail state.
    autoOpenOnFail = true,
}) {
    const [open, setOpen] = useState(defaultOpen);
    // Track the last fail-verdict we auto-opened for, so we re-trigger
    // on transitions ok → fail but not on every render.
    const lastAutoOpenVerdictRef = useRef(null);

    useEffect(() => {
        if (!autoOpenOnFail) return;
        if (isFailVerdict(verdict) && lastAutoOpenVerdictRef.current !== verdict) {
            lastAutoOpenVerdictRef.current = verdict;
            setOpen(true);
        }
        if (!isFailVerdict(verdict)) {
            lastAutoOpenVerdictRef.current = null;
        }
    }, [verdict, autoOpenOnFail]);

    const outline = verdictToClass(verdict);

    return (
        <div
            className={`brutal-card p-0 overflow-hidden border-2 transition-colors ${outline}`}
            data-testid={testId}
            data-open={open}
            data-verdict={verdict || "info"}
        >
            {/* div (not button): headerExtra may contain buttons — nested <button> is invalid HTML */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen(!open)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
                data-testid={`${testId}-toggle-btn`}
                className="w-full p-3 flex items-center justify-between hover:bg-zinc-900/50 transition-colors cursor-pointer select-none"
            >
                <span className="flex items-center gap-2 min-w-0 text-left flex-wrap">
                    {Icon && <Icon className="w-4 h-4 text-volt shrink-0" />}
                    <span className="font-display font-extrabold text-sm uppercase tracking-wide text-white">{title}</span>
                    {badge && (
                        <span className={`pill text-[9px] font-bold uppercase border ${badgeClass}`}>{badge}</span>
                    )}
                    {subtitle && <span className="text-[10px] text-zinc-500 truncate">· {subtitle}</span>}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                    {headerExtra}
                    <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
            </div>
            {open && (
                <div className="p-4 border-t border-zinc-800/80" data-testid={`${testId}-body`}>
                    {children}
                </div>
            )}
        </div>
    );
}
